import React, { useState } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { Eye, EyeOff, Loader } from 'lucide-react';
import AnimatedLogo from '../common/AnimatedLogo';
import { useAuthStore } from '../../store';
import toast from 'react-hot-toast';
import Button from '../common/Button';

interface LoginForm {
  username: string;
  password: string;
}

const LoginPage: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { login, isLoading, user } = useAuthStore();
  const rawReturnTo = (location.state as { from?: string })?.from;
  // Validate returnTo is a safe internal path (prevent open redirect)
  const returnTo = rawReturnTo && rawReturnTo.startsWith('/') && !rawReturnTo.startsWith('//') ? rawReturnTo : undefined;
  const [showPassword, setShowPassword] = useState(false);

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<LoginForm>();

  const onSubmit = async (data: LoginForm) => {
    try {
      await login(data.username, data.password);
      // Load projects before showing welcome message
      // Import dynamically to avoid coupling with store mock in tests
      const { useProjectStore } = await import('../../store');
      await useProjectStore.getState().loadProjects();
      // Use has_created_project flag from user object
      if (user && user.has_created_project === false) {
        toast.success("Welcome to Agent Commander! Let's get started with your first project.");
      } else {
        toast.success('Welcome back!');
      }
      navigate(returnTo || '/dashboard');
      // Dispatch salute after navigation so the sidebar AnimatedLogo is mounted
      setTimeout(() => window.dispatchEvent(new CustomEvent('agent-salute')), 300);
    } catch (error: any) {
      // Extract the error message from the response if available
      const errorMessage = error.response?.data?.detail || error.message || 'Login failed. Please try again.';
      toast.error(errorMessage);
      
      // Clear the password field on error
      const passwordInput = document.querySelector('input[name="password"]') as HTMLInputElement;
      if (passwordInput) {
        passwordInput.value = '';
      }
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-night py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-md w-full space-y-8 shadow rounded-lg p-6 sm:p-8 mx-4 sm:mx-auto dark:bg-cap-dark">
        {/* Header */}
        <div className="text-center">
          <div className="flex justify-center">
            <AnimatedLogo size={64} />
          </div>
          <h2 className="mt-6 text-2xl sm:text-3xl font-bold text-gray-900 dark:text-warm-white">
            Sign in to Agent Commander
          </h2>
          <p className="mt-2 text-sm text-gray-600 dark:text-gray-400">
            Take command of your agent workforce
          </p>
        </div>

        {/* Form */}
        <form className="mt-8 space-y-6" onSubmit={handleSubmit(onSubmit)}>
          <div className="space-y-4">
            {/* Username */}
            <div>
              <label htmlFor="username" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                Username
              </label>
              <input
                id="username"
                {...register('username', {
                  required: 'Username is required',
                  minLength: {
                    value: 3,
                    message: 'Username must be at least 3 characters',
                  },
                })}
                type="text"
                className={`input mt-1 ${errors.username ? 'border-red-300' : ''}`}
                placeholder="Enter your username"
              />
              {errors.username && (
                <p className="mt-1 text-sm text-red-600 dark:text-red-400">{errors.username.message}</p>
              )}
            </div>

            {/* Password */}
            <div>
              <label htmlFor="password" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                Password
              </label>
              <div className="relative mt-1">
                <input
                  id="password"
                  {...register('password', {
                    required: 'Password is required',
                    minLength: {
                      value: 6,
                      message: 'Password must be at least 6 characters',
                    },
                  })}
                  type={showPassword ? 'text' : 'password'}
                  className={`input pr-10 ${errors.password ? 'border-red-300' : ''}`}
                  placeholder="Enter your password"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  aria-label={showPassword ? 'Hide password' : 'Show password'}
                  className="absolute inset-y-0 right-0 pr-3 flex items-center min-w-[48px] min-h-[48px] justify-center"
                >
                  {showPassword ? (
                    <EyeOff className="w-5 h-5 text-gray-400" />
                  ) : (
                    <Eye className="w-5 h-5 text-gray-400" />
                  )}
                </button>
              </div>
              {errors.password && (
                <p className="mt-1 text-sm text-red-600 dark:text-red-400">{errors.password.message}</p>
              )}
            </div>
          </div>

          {/* Submit Button */}
          <Button
            type="submit"
            disabled={isLoading}
            className="btn btn-primary bg-primary-600 w-full"
          >
            {isLoading ? (
              <>
                <Loader className="w-4 h-4 mr-2 animate-spin" />
                Signing in...
              </>
            ) : (
              'Sign in'
            )}
          </Button>

          {/* Register Link */}
          <div className="text-center">
            <p className="text-sm text-gray-600 dark:text-gray-400">
              New to Agent Commander?{' '}
              <Link
                to="/register"
                className="font-medium text-primary-600 hover:text-primary-500"
              >
                Sign up to create an account
              </Link>
            </p>
          </div>
        </form>
      </div>
    </div>
  );
};

export default LoginPage;